/**
 * An undoable change: a 6 s toast with a CANCEL action; the change is confirmed
 * when the toast closes without it, cancelled otherwise. Opening a new one
 * dismisses the previous one first.
 */
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { snackbar } from '@/shared/ui/snackbar';

export const UNDO_DURATION_MS = 6000;
const DISMISS_SETTLE_MS = 500;

export interface UndoableChange {
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const useUndoToast = () => {
  const active = useRef<string | number | null>(null);

  return useCallback(async ({ label, onConfirm, onCancel }: UndoableChange) => {
    if (active.current !== null) {
      toast.dismiss(active.current);
      active.current = null;
      await new Promise((resolve) => setTimeout(resolve, DISMISS_SETTLE_MS));
    }
    let cancelled = false;
    const finish = () => {
      active.current = null;
      if (cancelled) {
        snackbar(`${label} cancelled`);
        onCancel();
        return;
      }
      snackbar(`${label} validated`);
      onConfirm();
    };
    active.current = toast(label, {
      position: 'bottom-left',
      duration: UNDO_DURATION_MS,
      testId: 'order-undo-toast',
      action: {
        label: 'CANCEL',
        onClick: () => {
          cancelled = true;
        },
      },
      onDismiss: finish,
      onAutoClose: finish,
    });
  }, []);
};
